import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	type DialogProps,
	Divider,
	Grid,
	Input,
	List,
	makeStyles,
	Typography,
} from "@material-ui/core";
import type { GridApi } from "ag-grid-community";
import { useUploadFiles } from "./hooks/useUploadFiles";
import { useEffect, useState } from "react";
import { useWorkOrderModalsContext } from "@/shared/work-orders-modals/context/work-order-modals.context";
import { useWorkOrderAttachmentGridActionsAndButtons } from "../../hooks/useWorkOrderAttachmentsGridActionsAndButtons";
import { ErrorModal, MESApiService } from "@dexteel/mesf-core";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import type { WorkOrderAttachment } from "@/pages/work-orders/models/WorkOrderAttachment";
import { linkWorkOrderAttachments } from "@/pages/work-orders/repositorie/WorkOrdersRepositorie";

const useStyles = makeStyles((theme) => ({
	root: {
		"& .input-group-text": {
			display: "none",
		},
		"& .MuiDialogContent-dividers": {
			padding: 0,
		},
	},
	formControl: {
		margin: theme.spacing(2),
		minWidth: 120,
	},
}));

type Props = {
	WorkOrderId: number;
	gridRef: GridApi | null;
	searchWorkOrderAttachment: Function;
	setRemoveAttachmentError: (err: string) => void;
	setListMessage: (err: string) => void | null;
	setShowLinkResourcesToAssetDialog: Function;
	setNewUploadedAttachments: Function;
};

export const UploadFilePage = ({
	WorkOrderId,
	gridRef,
	searchWorkOrderAttachment,
	setRemoveAttachmentError,
	setListMessage,
	setShowLinkResourcesToAssetDialog,
	setNewUploadedAttachments,
}: Props) => {
	const {
		handleFileSelect,
		handleDragOver,
		handleDrop,
		handleCancel,
		setIsDragging,
		setSelectedFiles,
		selectedFiles,
	} = useUploadFiles();

	const classes = useStyles();
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
	const [fullWidth, setFullWidth] = useState(true);
	const [maxWidth, setMaxWidth] = useState<DialogProps["maxWidth"]>("sm");
	const [attachmentsToAdd, setAttachmentsToAdd] = useState([]);
	const {
		state: { assetAttachments },
		actions: { setUploadFileDialog },
	} = useWorkOrderModalsContext();

	const { refresh } = useWorkOrderAttachmentGridActionsAndButtons({
		gridRef,
		searchWorkOrderAttachment,
		setRemoveAttachmentError,
		setListMessage,
	});
	const [
		linkAssetAttachmentsLoadingError,
		setLinkAssetAttachmentsLoadingError,
	] = useState<string>("");
	const [progressMap, setProgressMap] = useState<Record<string, number>>({});

	const handleRemoveFile = (fileNameToRemove: string) => {
		const newArr = selectedFiles.filter(
			(file: any) => file.name !== fileNameToRemove,
		);
		setSelectedFiles(newArr);
	};

	const api = new MESApiService();

	const handleProgress = (event: any) => {
		if (
			event &&
			event.target &&
			event.target.files &&
			event.target.files.length > 0
		) {
			Array.from<File>(event.target.files).forEach((file: File) => {
				const fileName = file.name;

				const reader = new FileReader();

				reader.onloadend = () => {
					setProgressMap((prevProgressMap) => ({
						...prevProgressMap,
						[fileName]: 100,
					}));
				};

				reader.onprogress = (e: ProgressEvent<FileReader>) => {
					if (e.lengthComputable) {
						const fileProgress = Math.round((e.loaded / e.total) * 100);
						setProgressMap((prevProgressMap) => ({
							...prevProgressMap,
							[fileName]: fileProgress,
						}));
					}
				};

				reader.readAsDataURL(file);
			});
		}
	};

	const handleDropWithProgress = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();

		const files = e.dataTransfer.files;

		if (files.length > 0) {
			Array.from(files).forEach((file: File) => {
				const fileName = file.name;

				const reader = new FileReader();

				reader.onloadend = () => {
					setProgressMap((prevProgressMap) => ({
						...prevProgressMap,
						[fileName]: 100,
					}));
				};

				reader.onprogress = (progressEvent: ProgressEvent<FileReader>) => {
					if (progressEvent.lengthComputable) {
						const fileProgress = Math.round(
							(progressEvent.loaded / progressEvent.total) * 100,
						);
						setProgressMap((prevProgressMap) => ({
							...prevProgressMap,
							[fileName]: fileProgress,
						}));
					}
				};

				reader.readAsDataURL(file);
			});

			handleDrop(e);
			setIsDragging(false);
		}
	};

	const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (selectedFiles.length === 0) return;

		setIsSubmitLoading(true);
		try {
			const uploadedFiles = await api.uploadFiles(selectedFiles, "Attachments");
			const formattedAttachments = uploadedFiles.result.map((att: any) => ({
				UploadFileId: att.UploadFileId,
				FileName: att.FileName,
				IsAssetResource: false,
			}));

			const res = await linkWorkOrderAttachments(
				WorkOrderId,
				formattedAttachments,
			);

			if (res.ok) {
				const newAttachments = formattedAttachments.filter(
					(newAtt: any) =>
						!assetAttachments.some(
							(assetAtt: any) => assetAtt.UploadFileId === newAtt.UploadFileId,
						),
				);

				if (newAttachments.length > 0) {
					setNewUploadedAttachments(newAttachments);
					setShowLinkResourcesToAssetDialog(true);
				}

				refresh();
				setUploadFileDialog(false);
			} else {
				setLinkAssetAttachmentsLoadingError(res.message);
			}
		} catch (error) {
			setLinkAssetAttachmentsLoadingError(
				"An error occurred while uploading and linking attachments",
			);
		} finally {
			setIsSubmitLoading(false);
			setSelectedFiles([]);
		}
	};

	useEffect(() => {
		setIsSubmitDisabled(selectedFiles.length === 0);
	}, [selectedFiles]);

	useEffect(() => {
		if (attachmentsToAdd?.length >= 1) {
			setIsSubmitLoading(true);
			const linkAttachments = async () => {
				try {
					const formattedAttachments = attachmentsToAdd.map(
						(att: WorkOrderAttachment) => ({
							UploadFileId: att.UploadFileId,
							FileName: att.FileName,
							IsAssetResource: false,
						}),
					);

					const res = await linkWorkOrderAttachments(
						WorkOrderId as number,
						formattedAttachments,
					);

					if (res.ok) {
						setIsSubmitLoading(false);
					} else {
						setLinkAssetAttachmentsLoadingError(res.message);
						setIsSubmitLoading(false);
					}
				} catch (error) {
					setLinkAssetAttachmentsLoadingError(
						"An error occurred while linking attachments",
					);
					setIsSubmitLoading(false);
				}
			};

			linkAttachments();
		} else {
			setIsSubmitDisabled(true);
		}
	}, [attachmentsToAdd, WorkOrderId]);

	useEffect(() => {
		setIsSubmitDisabled(selectedFiles.length === 0);
	}, [selectedFiles]);

	useEffect(() => {
		if (attachmentsToAdd?.length >= 1) {
			setIsSubmitLoading(true);
			const linkAttachments = async () => {
				try {
					const formattedAttachments = attachmentsToAdd.map(
						(att: WorkOrderAttachment) => ({
							UploadFileId: att.UploadFileId,
							FileName: att.FileName,
							IsAssetResource: false,
						}),
					);

					const res = await linkWorkOrderAttachments(
						WorkOrderId as number,
						formattedAttachments,
					);

					if (res.ok) {
						setIsSubmitLoading(false);
					} else {
						setLinkAssetAttachmentsLoadingError(res.message);
						setIsSubmitLoading(false);
					}
				} catch (error) {
					setLinkAssetAttachmentsLoadingError(
						"An error occurred while linking attachments",
					);
					setIsSubmitLoading(false);
				}
			};

			linkAttachments();
		} else {
			setIsSubmitDisabled(true);
		}
	}, [attachmentsToAdd, WorkOrderId]);

	return (
		<>
			<Dialog
				onClose={() => handleCancel()}
				fullWidth={fullWidth}
				maxWidth={maxWidth}
				open={true}
				className={classes.root}
			>
				<form onSubmit={handleFormSubmit}>
					<Grid
						container
						style={{ padding: "15px 0 5px", height: "30%", overflow: "hidden" }}
					>
						<Grid
							container
							item
							md={12}
							xs={12}
							style={{
								alignItems: "center",
								marginTop: 0,
								padding: 0,
								margin: 0,
							}}
							spacing={2}
						>
							<Grid
								container
								item
								md={12}
								xs={12}
								alignItems="center"
								style={{ padding: "8px 20px 8px" }}
							>
								<Grid item md={12} xs={12}>
									<div
										className={classes.formControl}
										onDragOver={(e) => {
											handleDragOver(e);
										}}
										onDragEnter={() => setIsDragging(true)}
										onDragLeave={() => setIsDragging(false)}
										onDragEnd={() => setIsDragging(false)}
										onDrop={(e) => {
											handleDrop(e);
											setIsDragging(false);
											handleDropWithProgress(e);
										}}
										style={{
											width: "100%",
											margin: "0",
											border: "2px dashed #3F51B5",
											borderRadius: "5px",
											padding: "20px",
											textAlign: "center",
											cursor: "pointer",
										}}
										onClick={() =>
											document.getElementById("fileInput")?.click()
										}
									>
										<Typography>Drag & Drop Files here</Typography>
										<Typography>or</Typography>
										<Typography>Click Here</Typography>
										<Input
											type="file"
											onChange={(event: any) => {
												handleFileSelect(event);
												handleProgress(event);
											}}
											style={{ display: "none" }}
											id="fileInput"
											inputProps={{ multiple: true }}
										/>
									</div>
								</Grid>
							</Grid>

							{selectedFiles.length >= 1 && (
								<Grid
									container
									item
									md={12}
									xs={12}
									style={{ marginTop: "10px", marginLeft: 20, padding: "0" }}
								>
									<Grid
										item
										md={12}
										xs={12}
										style={{
											justifyContent: "flex-start !important",
											alignItems: "flex-start",
										}}
									>
										<Typography style={{ fontWeight: "bold" }}>
											Document Selected:
										</Typography>

										<List
											style={{
												height: "20vh",
												margin: "10px 20px 20px 0",
												padding: "10px",
												overflow: "auto",
												background: "#FCFCFC",
											}}
										>
											{selectedFiles.map((file: any) => (
												<>
													<div
														key={`file-${file.name}`}
														style={{
															textAlign: "left",
															display: "flex",
															alignItems: "center",
															padding: 0,
															margin: "5px 0",
														}}
													>
														<span
															style={{ marginRight: 5, cursor: "pointer" }}
															onClick={() => handleRemoveFile(file.name)}
														>
															<DeleteForeverIcon />
														</span>
														<p
															style={{
																margin: "5px 0",
																wordBreak: "break-all",
															}}
														>
															{file.name} ({(file.size / 1024).toFixed(2)} KB)
														</p>
													</div>
													{progressMap[file.name] > 0 && (
														<div
															style={{
																width: "100%",
																height: 20,
																backgroundColor: "#f2f2f2",
																borderRadius: 5,
															}}
														>
															<div
																style={{
																	width: `${progressMap[file.name]}%`,
																	height: "100%",
																	backgroundColor: `rgba(63, 81, 181, ${
																		progressMap[file.name] / 100
																	})`,
																	borderRadius: "inherit",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	color: "white",
																	fontWeight: "bold",
																}}
															>
																<span style={{ fontSize: "12px" }}>{`${
																	progressMap[file.name]
																}%`}</span>
															</div>
														</div>
													)}
												</>
											))}
										</List>
									</Grid>
								</Grid>
							)}
							<Divider
								variant="middle"
								style={{
									margin: "10px 0",
									width: "150%",
									marginLeft: "-100px",
								}}
							/>
							<Grid item md={12} xs={12} justifyContent="flex-end">
								<DialogActions style={{ padding: "0px 10px " }}>
									<Grid
										container
										justifyContent="flex-end"
										style={{ margin: 0 }}
									>
										<Grid
											item
											md={3}
											xs={4}
											style={{ margin: 0, padding: "2px 0 8px 0" }}
										>
											<Button
												fullWidth
												variant="contained"
												color="default"
												onClick={() => setUploadFileDialog(false)}
											>
												Cancel
											</Button>
										</Grid>
										<Grid
											item
											md={3}
											xs={4}
											style={{
												margin: 0,
												padding: "2px 0 8px 0",
												marginLeft: "20px",
											}}
										>
											<Button
												fullWidth
												startIcon={
													isSubmitLoading && <CircularProgress size="1rem" />
												}
												disabled={isSubmitLoading || isSubmitDisabled}
												variant="contained"
												color="primary"
												type="submit"
											>
												Save
											</Button>
										</Grid>
									</Grid>
								</DialogActions>
							</Grid>
						</Grid>
					</Grid>
				</form>
			</Dialog>
			<ErrorModal
				error={linkAssetAttachmentsLoadingError}
				onHide={() => setLinkAssetAttachmentsLoadingError("")}
			/>
		</>
	);
};
